import React from 'react';
import { Lock, Shield, Zap } from 'lucide-react';

interface FeatureCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  glowColor: string;
}

const features: FeatureCard[] = [
  {
    icon: <Lock className="w-8 h-8" />,
    title: 'Irreversible Transformation',
    description:
      'Like matter crossing an event horizon, your data undergoes a one-way cryptographic hash. The original information is mathematically irretrievable, yet its essence persists eternally in the proof.',
    gradient: 'from-purple-500 via-purple-600 to-indigo-700',
    glowColor: 'purple',
  },
  {
    icon: <Shield className="w-8 h-8" />,
    title: 'Holographic Preservation',
    description:
      'Your proof acts as a holographic imprint—containing all the verification data needed without exposing the original content. Verify authenticity while maintaining complete privacy.',
    gradient: 'from-pink-500 via-rose-500 to-purple-600',
    glowColor: 'pink',
  },
  {
    icon: <Zap className="w-8 h-8" />,
    title: 'Cryptographic Proofs',
    description:
      'Leveraging zero-knowledge principles, prove you possess information without revealing it. Like quantum entanglement, the proof and original remain forever linked across space and time.',
    gradient: 'from-blue-500 via-cyan-500 to-teal-500',
    glowColor: 'blue',
  },
];

export const FeaturesSection: React.FC = () => {
  return (
    <section className="relative py-20 px-4">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent pointer-events-none" />

      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
            The Science of Digital Preservation
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Explore the cryptographic principles that make eternal verification possible
          </p>
        </div>

        {/* Feature cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <FeatureCardComponent key={index} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

interface FeatureCardProps {
  feature: FeatureCard;
  index: number;
}

const FeatureCardComponent: React.FC<FeatureCardProps> = ({ feature, index }) => {
  const getGlowStyles = (color: string): string => {
    switch (color) {
      case 'purple':
        return 'hover:shadow-purple-500/30';
      case 'pink':
        return 'hover:shadow-pink-500/30';
      case 'blue':
        return 'hover:shadow-blue-500/30';
      default:
        return 'hover:shadow-purple-500/30';
    }
  };

  const getIconBgGradient = (color: string): string => {
    switch (color) {
      case 'purple':
        return 'bg-gradient-to-br from-purple-500/20 to-indigo-600/20 group-hover:from-purple-500/30 group-hover:to-indigo-600/30';
      case 'pink':
        return 'bg-gradient-to-br from-pink-500/20 to-rose-600/20 group-hover:from-pink-500/30 group-hover:to-rose-600/30';
      case 'blue':
        return 'bg-gradient-to-br from-blue-500/20 to-cyan-600/20 group-hover:from-blue-500/30 group-hover:to-cyan-600/30';
      default:
        return 'bg-gradient-to-br from-purple-500/20 to-indigo-600/20';
    }
  };

  const getIconTextColor = (color: string): string => {
    switch (color) {
      case 'purple':
        return 'text-purple-400 group-hover:text-purple-300';
      case 'pink':
        return 'text-pink-400 group-hover:text-pink-300';
      case 'blue':
        return 'text-blue-400 group-hover:text-blue-300';
      default:
        return 'text-purple-400';
    }
  };

  return (
    <div
      className={`
        group relative
        bg-gray-900/40 backdrop-blur-xl
        border border-gray-700/50
        rounded-2xl p-6 lg:p-8
        transition-all duration-500 ease-out
        hover:scale-[1.02] hover:-translate-y-1
        hover:border-gray-600/50
        hover:shadow-2xl ${getGlowStyles(feature.glowColor)}
        cursor-default
      `}
      style={{
        animationDelay: `${index * 100}ms`,
      }}
    >
      {/* Gradient overlay on hover */}
      <div
        className={`
          absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-10
          bg-gradient-to-br ${feature.gradient}
          transition-opacity duration-500 pointer-events-none
        `}
      />

      {/* Icon container */}
      <div
        className={`
          relative w-16 h-16 mb-6
          rounded-xl
          ${getIconBgGradient(feature.glowColor)}
          border border-gray-700/50 group-hover:border-gray-600/50
          flex items-center justify-center
          transition-all duration-300
        `}
      >
        <div className={`${getIconTextColor(feature.glowColor)} transition-colors duration-300`}>
          {feature.icon}
        </div>

        {/* Icon glow effect */}
        <div
          className={`
            absolute inset-0 rounded-xl
            bg-gradient-to-br ${feature.gradient}
            opacity-0 group-hover:opacity-20
            blur-xl transition-opacity duration-500
          `}
        />
      </div>

      {/* Content */}
      <div className="relative">
        <h3
          className={`
            text-xl font-semibold mb-3
            bg-gradient-to-r ${feature.gradient}
            bg-clip-text text-transparent
          `}
        >
          {feature.title}
        </h3>

        <p className="text-gray-400 leading-relaxed text-sm lg:text-base group-hover:text-gray-300 transition-colors duration-300">
          {feature.description}
        </p>
      </div>

      {/* Bottom accent line */}
      <div
        className={`
          absolute bottom-0 left-1/2 -translate-x-1/2
          w-0 h-0.5 rounded-full
          bg-gradient-to-r ${feature.gradient}
          group-hover:w-1/2
          transition-all duration-500
        `}
      />
    </div>
  );
};

export default FeaturesSection;
